import { AudioBufferSink, AudioBufferSource } from "mediabunny";

export type AudioExportItem = {
    startSec: number;
    inSec: number;
    outSec: number;
    getAudioTrack?: () => Promise<any>;
    gain?: number;
    rate?: number;
    kind?: "media" | "silence";
    sourceKey?: string;
};

export const DEFAULT_TARGET_SAMPLE_RATE = 48000;
export const DEFAULT_TARGET_CHANNELS = 2;

const EPS = 1e-3;
const EMPTY_CHANNEL = new Float32Array(0);

const resolveTargetSampleRate = (value?: number): 44100 | 48000 =>
    Number(value) === 44100 ? 44100 : 48000;

const resolveTargetChannels = (value?: number): 1 | 2 =>
    Number(value) === 1 ? 1 : 2;

const makeSilence = (sec: number, sampleRate: number, channels: number): AudioBuffer | null => {
    const frames = Math.max(0, Math.floor(sec * sampleRate));
    if (frames <= 0) return null;
    return new AudioBuffer({
        length: frames,
        numberOfChannels: channels,
        sampleRate,
    });
};

const sliceBuffer = (buf: AudioBuffer, startSec: number, endSec: number): AudioBuffer | null => {
    const sr = buf.sampleRate;
    const sFrame = Math.max(0, Math.floor(startSec * sr));
    const eFrame = Math.min(buf.length, Math.floor(endSec * sr));
    const frames = Math.max(0, eFrame - sFrame);
    if (frames === 0) return null;
    const out = new AudioBuffer({
        length: frames,
        numberOfChannels: buf.numberOfChannels,
        sampleRate: sr,
    });
    for (let ch = 0; ch < buf.numberOfChannels; ch++) {
        const tmp = new Float32Array(frames);
        buf.copyFromChannel(tmp, ch, sFrame);
        out.copyToChannel(tmp, ch, 0);
    }
    return out;
};

const sampleLinear = (channel: Float32Array, index: number): number => {
    if (channel.length === 0) return 0;
    if (index <= 0) return channel[0] || 0;
    const last = channel.length - 1;
    if (index >= last) return channel[last] || 0;
    const i0 = Math.floor(index);
    const i1 = Math.min(i0 + 1, last);
    const t = index - i0;
    return (channel[i0] || 0) * (1 - t) + (channel[i1] || 0) * t;
};

const normalizeAudioBuffer = (args: {
    buffer: AudioBuffer;
    playbackRate?: number;
    targetSampleRate: number;
    targetChannels: number;
}): AudioBuffer => {
    const { buffer: buf, targetChannels, targetSampleRate } = args;
    const srcRate = buf.sampleRate;
    const srcChannels = buf.numberOfChannels;
    const rate = Number(args.playbackRate) || 1.0;

    if (rate === 1.0 && srcRate === targetSampleRate && srcChannels === targetChannels) {
        return buf;
    }

    const ratio = (targetSampleRate / srcRate) / Math.max(0.001, rate);
    const outLength = Math.floor(buf.length * ratio);
    if (outLength === 0) return buf;

    const out = new AudioBuffer({
        length: outLength,
        numberOfChannels: targetChannels,
        sampleRate: targetSampleRate,
    });

    const sourceData: Float32Array[] = [];
    for (let ch = 0; ch < srcChannels; ch++) sourceData.push(buf.getChannelData(ch));
    const outData: Float32Array[] = [];
    for (let ch = 0; ch < targetChannels; ch++) outData.push(out.getChannelData(ch));

    for (let i = 0; i < outLength; i++) {
        const srcIdx = i / ratio;
        const left = sampleLinear(sourceData[0] ?? EMPTY_CHANNEL, srcIdx);
        const right = srcChannels > 1
            ? sampleLinear(sourceData[1], srcIdx)
            : left;

        if (targetChannels === 1) {
            outData[0][i] = (left + right) * 0.5;
            continue;
        }

        outData[0][i] = left;
        outData[1][i] = right;
    }

    return out;
};

const mixInto = (
    dstChannels: Float32Array[],
    src: AudioBuffer,
    dstStartFrame: number,
    gain: number,
): void => {
    if (dstChannels.length === 0) return;
    const first = dstChannels[0];
    if (!Number.isFinite(dstStartFrame) || dstStartFrame >= first.length) return;
    const start = Math.max(0, Math.floor(dstStartFrame));
    const maxFrames = Math.min(src.length, first.length - start);
    if (maxFrames <= 0) return;

    const g = Number.isFinite(gain) ? gain : 1.0;
    const srcData: Float32Array[] = [];
    for (let ch = 0; ch < src.numberOfChannels; ch++) srcData.push(src.getChannelData(ch));

    for (let ch = 0; ch < dstChannels.length; ch++) {
        const dst = dstChannels[ch];
        const fallback = srcData[0] ?? EMPTY_CHANNEL;
        const srcCh = srcData[Math.min(ch, srcData.length - 1)] ?? fallback;
        for (let i = 0; i < maxFrames; i++) {
            dst[start + i] += (srcCh[i] || 0) * g;
        }
    }
};

const softClip = (v: number): number => Math.tanh(v);

const overlapDuration = (item: AudioExportItem): number => {
    const srcDur = Math.max(0, Number(item.outSec ?? 0) - Number(item.inSec ?? 0));
    const rate = Math.max(0.001, Number(item.rate ?? 1));
    return srcDur / rate;
};

const sourceKeyFor = (item: AudioExportItem, idx: number): string => {
    return String(item.sourceKey ?? `item_${idx}`);
};

const getItemSinkFactory = (): ((item: AudioExportItem, index: number) => Promise<AudioBufferSink | null>) => {
    const sinkByKey = new Map<string, Promise<AudioBufferSink | null>>();

    return async (item: AudioExportItem, index: number): Promise<AudioBufferSink | null> => {
        if (item.kind === "silence" || typeof item.getAudioTrack !== "function") return null;
        const key = sourceKeyFor(item, index);
        let promise = sinkByKey.get(key);
        if (!promise) {
            promise = (async () => {
                try {
                    const track = await item.getAudioTrack!();
                    if (!track) return null;
                    const canDecode = await track.canDecode();
                    if (!canDecode) return null;
                    return new AudioBufferSink(track);
                } catch {
                    return null;
                }
            })();
            sinkByKey.set(key, promise);
        }
        return promise;
    };
};

const mixItemOverlapIntoWindow = async (args: {
    item: AudioExportItem;
    sink: AudioBufferSink;
    windowStartSec: number;
    windowEndSec: number;
    mixChannels: Float32Array[];
    targetSampleRate: number;
    targetChannels: number;
    signal?: AbortSignal;
}): Promise<void> => {
    const {
        item,
        sink,
        windowStartSec,
        windowEndSec,
        mixChannels,
        targetSampleRate,
        targetChannels,
        signal,
    } = args;
    const itemStart = Number(item.startSec ?? 0);
    const itemEnd = itemStart + overlapDuration(item);
    if (!(itemEnd > windowStartSec + EPS && itemStart < windowEndSec - EPS)) return;

    const overlapStart = Math.max(itemStart, windowStartSec);
    const overlapEnd = Math.min(itemEnd, windowEndSec);
    if (overlapEnd <= overlapStart + EPS) return;

    const rate = Math.max(0.001, Number(item.rate ?? 1));
    const srcStart = Number(item.inSec ?? 0) + (overlapStart - itemStart) * rate;
    const srcEnd = srcStart + (overlapEnd - overlapStart) * rate;
    if (!(Number.isFinite(srcStart) && Number.isFinite(srcEnd) && srcEnd > srcStart + EPS)) return;

    const gain = Number(item.gain ?? 1.0) || 1.0;

    let remainingSourceSec = srcEnd - srcStart;
    let dstFrameCursor = Math.max(0, Math.floor((overlapStart - windowStartSec) * targetSampleRate));

    for await (const wrapped of sink.buffers(srcStart, srcEnd)) {
        if (signal?.aborted) return;
        const buffer = wrapped?.buffer;
        if (!buffer) break;

        if (remainingSourceSec <= EPS) break;
        const bufferSourceDur = buffer.length / buffer.sampleRate;
        let toAdd = buffer;
        if (bufferSourceDur > remainingSourceSec + EPS) {
            const sliced = sliceBuffer(buffer, 0, remainingSourceSec);
            if (!sliced) break;
            toAdd = sliced;
        }

        const normalized = normalizeAudioBuffer({
            buffer: toAdd,
            playbackRate: rate,
            targetChannels,
            targetSampleRate,
        });
        mixInto(mixChannels, normalized, dstFrameCursor, gain);

        dstFrameCursor += normalized.length;
        remainingSourceSec -= toAdd.length / toAdd.sampleRate;
    }
};

/**
 * Mixes timeline audio items into a single encoded track.
 * Unlike linear append, this supports true overlap mixing (same as preview behavior).
 */
export async function writeMixedAudioTrack(args: {
    audioSource: AudioBufferSource;
    items: AudioExportItem[];
    durationSec: number;
    targetChannels?: number;
    targetSampleRate?: number;
    signal?: AbortSignal;
    windowSec?: number;
}): Promise<void> {
    const { audioSource, signal } = args;
    const durationSec = Math.max(0, Number(args.durationSec ?? 0));
    const targetChannels = resolveTargetChannels(args.targetChannels);
    const targetSampleRate = resolveTargetSampleRate(args.targetSampleRate);
    const items = [...(args.items ?? [])]
        .filter((it) => Number.isFinite(it.startSec) && Number.isFinite(it.inSec) && Number.isFinite(it.outSec))
        .sort((a, b) => Number(a.startSec) - Number(b.startSec));

    const windowSec = Math.max(1, Math.floor(Number(args.windowSec ?? 20)));
    const getSink = getItemSinkFactory();

    if (durationSec <= EPS) return;
    if (signal?.aborted) return;

    // Fast path: no media at all -> emit a single silence span.
    const hasMedia = items.some((it) => it.kind !== "silence" && typeof it.getAudioTrack === "function");
    if (!hasMedia) {
        const silence = makeSilence(durationSec, targetSampleRate, targetChannels);
        if (silence) await audioSource.add(silence);
        return;
    }

    for (let windowStartSec = 0; windowStartSec < durationSec - EPS; windowStartSec += windowSec) {
        if (signal?.aborted) return;
        const windowEndSec = Math.min(durationSec, windowStartSec + windowSec);
        const windowFrames = Math.max(1, Math.ceil((windowEndSec - windowStartSec) * targetSampleRate));

        const mixChannels: Float32Array[] = [];
        for (let ch = 0; ch < targetChannels; ch++) {
            mixChannels.push(new Float32Array(windowFrames));
        }

        for (let i = 0; i < items.length; i++) {
            if (signal?.aborted) return;
            const item = items[i];
            if (item.kind === "silence" || typeof item.getAudioTrack !== "function") continue;

            const itemStart = Number(item.startSec ?? 0);
            const itemEnd = itemStart + overlapDuration(item);
            if (itemEnd <= windowStartSec + EPS || itemStart >= windowEndSec - EPS) continue;

            const sink = await getSink(item, i);
            if (!sink) continue;

            await mixItemOverlapIntoWindow({
                item,
                sink,
                windowStartSec,
                windowEndSec,
                mixChannels,
                targetChannels,
                targetSampleRate,
                signal,
            });
        }

        for (let i = 0; i < windowFrames; i++) {
            for (let ch = 0; ch < mixChannels.length; ch++) {
                mixChannels[ch][i] = softClip(mixChannels[ch][i]);
            }
        }

        const out = new AudioBuffer({
            length: windowFrames,
            numberOfChannels: targetChannels,
            sampleRate: targetSampleRate,
        });
        for (let ch = 0; ch < mixChannels.length; ch++) {
            out.copyToChannel(mixChannels[ch], ch, 0);
        }
        await audioSource.add(out);
    }
}
